/**
 * 响应式容器组件
 * Feature: mobile-responsive-framework, Task 6: 实现响应式容器组件
 * 需求: 9.2
 */
import React from 'react';
import { useResponsiveConfig } from '../hooks/useResponsiveConfig';
import '../styles/responsive.css';

// 容器最大宽度类型
export type ContainerMaxWidth = 'sm' | 'md' | 'lg' | 'xl' | 'full' | number;

// 容器内边距类型
export type ContainerPadding = 'none' | 'sm' | 'md' | 'lg';

export interface ResponsiveContainerProps {
  children: React.ReactNode;
  maxWidth?: ContainerMaxWidth;
  padding?: ContainerPadding;
  className?: string;
  style?: React.CSSProperties;
  as?: keyof JSX.IntrinsicElements;
  centered?: boolean;
}

// 最大宽度映射
const MAX_WIDTH_MAP: Record<string, string> = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  full: '100%'
};

// 内边距映射
const PADDING_MAP: Record<ContainerPadding, string> = {
  none: '0',
  sm: 'var(--spacing-sm)',
  md: 'var(--spacing-md)',
  lg: 'var(--spacing-lg)'
};

/**
 * 响应式容器组件
 * 提供自适应宽度和内边距的容器
 */
export const ResponsiveContainer: React.FC<ResponsiveContainerProps> = ({
  children,
  maxWidth = 'xl',
  padding = 'md',
  className = '',
  style = {},
  as: Component = 'div',
  centered = true
}) => {
  const { containerPadding } = useResponsiveConfig();
  
  // 计算最大宽度
  const computedMaxWidth = typeof maxWidth === 'number' 
    ? `${maxWidth}px` 
    : MAX_WIDTH_MAP[maxWidth] || MAX_WIDTH_MAP.xl;
  
  // 计算内边距
  const computedPadding = padding === 'none' 
    ? '0' 
    : PADDING_MAP[padding] || `${containerPadding}px`;
  
  // 构建类名
  const containerClassName = [
    'responsive-container',
    maxWidth === 'full' && 'responsive-container--fluid',
    className
  ].filter(Boolean).join(' ');
  
  // 构建样式
  const containerStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: computedMaxWidth,
    padding: computedPadding,
    margin: centered ? '0 auto' : undefined,
    ...style
  };
  
  return (
    <Component className={containerClassName} style={containerStyle}>
      {children}
    </Component>
  );
};

export default ResponsiveContainer;
